import {Button, Dialog, DialogContent, IconButton, InputAdornment, Typography} from "@mui/material";
import {Visibility, VisibilityOff} from "@mui/icons-material";
import React, {useState} from "react";
import CCInputLabel from "../Common/CCInputLabel";
import CCTextField from "../Common/CCTextField";
import {useSessionContext} from "../../util/SessionContext";
import {changePassword, messageFor} from "../../util/changePassword";

/**
 * Lets a signed-in person change their own password.
 *
 * THE CURRENT PASSWORD IS ASKED FOR, AND THE SERVER CHECKS IT. A session proves the browser, not
 * the person: without this an unlocked laptop is a way to lock the owner out of their own account,
 * because the attacker sets a password only they know and the owner's own credential stops
 * working. This platform already works that way for signing -- a password proves the person -- and
 * this is the same rule.
 *
 * NOTHING HERE IS THE CHECK. The rules below are to save a round trip and to say what is wrong
 * while the user is still typing; the endpoint applies all of them again and is what actually
 * refuses. A client-side rule that the server does not also enforce is decoration.
 */
export default function ChangePasswordDialog({onClose}: {onClose: () => void}) {
    const sessionContext = useSessionContext();

    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [show, setShow] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [done, setDone] = useState(false);

    // Mirrors the endpoint: non-empty, no spaces, and actually different.
    const localProblem =
        !current ? null
        : !next ? null
        : next.includes(" ") ? "The new password cannot contain a space."
        : next === current ? "The new password has to be different from the current one."
        : confirm && next !== confirm ? "The two new passwords do not match."
        : null;

    const ready = !!current && !!next && next === confirm && !localProblem && !submitting;

    async function submit() {
        if (!ready) return;
        setSubmitting(true);
        setError(null);

        const result = await changePassword(
            "t13", sessionContext.getAccessToken(), current, next);

        setSubmitting(false);

        if (result.changed) {
            setDone(true);
            return;
        }
        setError(messageFor(result));
    }

    const field = (label: string, value: string, set: (v: string) => void, id: string) => (
        <>
            <CCInputLabel>{label}</CCInputLabel>
            <CCTextField
                id={id}
                size="small"
                fullWidth
                type={show ? "text" : "password"}
                value={value}
                autoComplete={id === "current-password" ? "current-password" : "new-password"}
                onChange={(e: any) => set(e.target.value)}
                onKeyDown={(e: any) => { if (e.key === "Enter") submit(); }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton size="small" tabIndex={-1} onClick={() => setShow(!show)}>
                                {show ? <Visibility fontSize="small"/> : <VisibilityOff fontSize="small"/>}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}/>
        </>
    );

    return (
        <Dialog maxWidth="xs" fullWidth open={true} onClose={onClose} scroll="body">
            <DialogContent style={{padding: "20px 24px 24px"}}>
                <Typography style={{fontSize: 20, fontWeight: 500, marginBottom: 4}}>
                    {done ? "Your password has been changed" : "Change password"}
                </Typography>

                {done ? (
                    <>
                        <Typography style={{fontSize: 14, color: "#525968", margin: "10px 0 20px"}}>
                            Use the new one next time you sign in. You are still signed in here, and
                            anywhere else you are already signed in stays signed in.
                        </Typography>
                        <Button variant="contained" fullWidth onClick={onClose}>Done</Button>
                    </>
                ) : (
                    <>
                        <Typography style={{fontSize: 14, color: "#525968", margin: "6px 0 16px"}}>
                            Enter your current password, then the new one twice.
                        </Typography>

                        {field("Current password", current, setCurrent, "current-password")}
                        <div style={{height: 10}}/>
                        {field("New password", next, setNext, "new-password")}
                        <div style={{height: 10}}/>
                        {field("Confirm new password", confirm, setConfirm, "confirm-password")}

                        {(error || localProblem) ? (
                            <Typography style={{fontSize: 13, color: "#C62828", marginTop: 14}}>
                                {error || localProblem}
                            </Typography>
                        ) : null}

                        <div style={{display: "flex", gap: 10, marginTop: 22}}>
                            <Button fullWidth onClick={onClose} disabled={submitting}>Cancel</Button>
                            <Button variant="contained" fullWidth disabled={!ready} onClick={submit}>
                                {submitting ? "Changing…" : "Change password"}
                            </Button>
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
